/**
 * D826 (owner 2026-10-28): a voice message of a ride chat, as the central listens to it in Admin › Chats da corrida. The recording is read only when the
 * operator presses "Ouvir" (`GET /v1/ops/rides/:rideId/messages/:messageId/audio`, `ride_chat:read` at the ride's market - the server logs the listen), kept
 * in memory as a blob URL for the page's life and released when the message leaves the screen. A progress bar (the design's track) follows the playback,
 * with the position and the length in tabular numerals. A recording already deleted by its retention reads "Áudio indisponível".
 */
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { Button, Cluster, ProgressTrack, Text } from '@vello/web-theme';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';

/** "0:07", "1:00". */
const clock = (seconds: number): string => {
  const s = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const toBlobUrl = (bytesBase64: string, contentType: string): string => {
  const binary = atob(bytesBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: contentType }));
};

export function VoiceMessagePlayer({ rideId, messageId, durationSeconds }: { readonly rideId: string; readonly messageId: string; readonly durationSeconds: number }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const audio = useRef<HTMLAudioElement | undefined>(undefined);
  const url = useRef<string | undefined>(undefined);
  const [phase, setPhase] = useState<'idle' | 'loading' | 'playing' | 'paused' | 'failed'>('idle');
  const [position, setPosition] = useState(0);
  const [length, setLength] = useState(durationSeconds);

  useEffect(
    () => () => {
      audio.current?.pause();
      if (url.current !== undefined) URL.revokeObjectURL(url.current);
    },
    [],
  );

  const toggle = async (): Promise<void> => {
    const current = audio.current;
    if (current !== undefined) {
      if (current.paused) {
        await current.play().catch(() => setPhase('failed'));
        setPhase('playing');
      } else {
        current.pause();
        setPhase('paused');
      }
      return;
    }
    setPhase('loading');
    const res = await runtime.centralDesk.rideChatAudio(rideId, messageId);
    if (!res.ok) {
      setPhase('failed');
      return;
    }
    url.current = toBlobUrl(res.value.bytesBase64, res.value.contentType);
    const el = new Audio(url.current);
    el.ontimeupdate = () => setPosition(el.currentTime);
    el.onloadedmetadata = () => {
      if (Number.isFinite(el.duration) && el.duration > 0) setLength(el.duration);
    };
    el.onended = () => {
      setPhase('paused');
      setPosition(0);
    };
    audio.current = el;
    try {
      await el.play();
      setPhase('playing');
    } catch {
      setPhase('failed');
    }
  };

  const playing = phase === 'playing';
  return (
    <Cluster gap={3} align="center" data-testid={`voice-message-${messageId}`}>
      <Button variant="secondary" size="sm" loading={phase === 'loading'} onClick={() => void toggle()} aria-label={t(playing ? 'cdesk.voice.pause' : 'cdesk.voice.play')} data-testid={`voice-message-${messageId}-toggle`}>
        {t(playing ? 'cdesk.voice.pause' : 'cdesk.voice.play')}
      </Button>
      <ProgressTrack fraction={position / Math.max(1, length)} size="sm" role="progressbar" ariaLabel={t('cdesk.voice.label', { seconds: Math.round(length) })} valueNow={Math.round(position)} valueMax={Math.round(length)} />
      <Text as="span" role="caption" tone="secondary" mono>
        {phase === 'failed' ? t('cdesk.voice.unavailable') : `${clock(position)} / ${clock(length)}`}
      </Text>
    </Cluster>
  );
}
